import * as React from "react";
import ReactDOM from "react-dom";
import { cx } from "utils/join-classnames";
import useOnClickOutside from "ui/hooks/useOnClickOutside";
import { Icon, Spinner, Button } from "ui/components";
import { DropdownOption } from "core/models";
import styles from "./Dropdown.module.scss";

let searchTimeout: NodeJS.Timeout;
interface Props {
    theme: "dark" | "light";
    placeholderText?: string;
    options: Array<DropdownOption>;
    onValueChanged(newValue: string | undefined): void;
    value?: string;
    disabled?: boolean;
    fullHeight?: boolean;
    fullWidth?: boolean;
    searchable?: boolean;
    onSearch?(query: string): void;
    pending?: boolean;
    className?: string;
}

const Dropdown: React.FC<Props> = ({
    theme,
    options,
    onValueChanged,
    value,
    disabled,
    fullHeight = false,
    fullWidth = false,
    searchable,
    onSearch,
    pending,
    placeholderText = "Select an option",
    className,
}: Props) => {
    const [isOpened, setIsOpened] = React.useState<boolean>(false);
    const dropdownRef = React.useRef<HTMLDivElement | null>(null);
    const headerRef = React.useRef<HTMLDivElement | null>(null);
    const [selectedOption, setSelectedOption] = React.useState<
        DropdownOption | undefined
    >(undefined);
    const [listStyle, setListStyle] = React.useState<React.CSSProperties>({
        width: "300px",
    });
    const containerRef = React.useRef<HTMLDivElement>(null);
    const listRef = React.useRef<HTMLDivElement>(null);
    const [scrollPos, setScrollPos] = React.useState<number>(0);
    const [userInteracted, setUserInteracted] = React.useState<boolean>(false);
    const [searchQuery, setSearchQuery] = React.useState<string | null>(null);

    const closeDropdown = React.useCallback((event: MouseEvent) => {
        if (!headerRef.current?.contains(event.target as Node)) {
            setIsOpened(false);
        }
    }, []);
    useOnClickOutside(listRef, closeDropdown);

    const clearSearch = (): void => {
        setSearchQuery(null);
        onValueChanged(undefined);
    };

    const toggleDropdown = () => {
        if (disabled) {
            return;
        }
        if (searchQuery) {
            setIsOpened(true);
            return;
        }
        setIsOpened(!isOpened);
    };

    const changeValue = (newValue: string | undefined) => {
        setIsOpened(false);
        setSearchQuery(null);
        onValueChanged(newValue);
        const newSelectedOption = options.find((x) => x.value === newValue);
        setSelectedOption(newSelectedOption);
        setUserInteracted(true);
    };

    React.useEffect(() => {
        if (disabled) {
            setSearchQuery(null);
            setUserInteracted(false);
            setIsOpened(false);
        }
    }, [disabled]);

    React.useEffect(() => {
        const activeOption = options.filter((option) => option.value === value);
        if (activeOption) {
            setSelectedOption(activeOption[0]);
        }
    }, [value, options]);

    const offsetByParentPos = (parentEl: HTMLDivElement) => {
        const rect = parentEl.getBoundingClientRect();
        const scrollLeft =
            window.pageXOffset || document.documentElement.scrollLeft;
        const scrollTop =
            window.pageYOffset || document.documentElement.scrollTop;
        return {
            top: rect.top + rect.height + scrollTop,
            left: rect.left + scrollLeft,
        };
    };

    React.useEffect(() => {
        if (containerRef.current && isOpened) {
            const listPos = offsetByParentPos(containerRef.current);
            const newListStyle = { ...listStyle, ...listPos };
            setListStyle(newListStyle);
        }
    }, [isOpened, scrollPos]);

    // Dropdown list width + pos fix
    // Because of portaling and 'fixed' position, which was needed so it doenst get cut off by any parents overflow setting.
    const adjustListWidth = () => {
        if (!headerRef.current) {
            return;
        }
        setListStyle({
            width: `${headerRef.current.offsetWidth + 2}px`,
        });
    };
    React.useEffect(() => {
        adjustListWidth();
    }, [headerRef.current?.offsetWidth]);
    const handleScroll = (e: React.UIEvent<HTMLElement>) => {
        setScrollPos(e.currentTarget.scrollTop);
    };
    React.useEffect(() => {
        window.addEventListener("resize", adjustListWidth);
        if (!listRef.current) {
            return undefined;
        }
        return () => {
            window.removeEventListener("resize", adjustListWidth);
        };
    }, []);

    React.useEffect(() => {
        clearTimeout(searchTimeout);
        if (!searchQuery) {
            return;
        }
        setIsOpened(true);
        searchTimeout = setTimeout(() => {
            if (onSearch) {
                onSearch(searchQuery);
            }
        }, 200);
    }, [onSearch, searchQuery]);
    const isLight = theme === "light";

    const listJSX = (
        <div
            className={cx(
                styles.list,
                isLight ? styles.list__light : null,
                fullHeight ? styles.list__fullheight : null,
                fullWidth ? styles.fullwidth : null,
            )}
            style={listStyle}
            ref={listRef}
            onScroll={handleScroll}
        >
            {options.map((x) => (
                <div
                    onClick={() => {
                        changeValue(x.value);
                    }}
                    key={x.id}
                    role="presentation"
                    className={cx(
                        styles.list__item,
                        isLight ? styles.list__item__light : null,
                        selectedOption && x.id === selectedOption.id
                            ? styles.list__item__selected
                            : null,
                        selectedOption && x.id === selectedOption.id && isLight
                            ? styles.list__item__selected__light
                            : null,
                    )}
                >
                    {x.iconName && <Icon name={x.iconName} />}

                    <span className={styles.list__item__text}>{x.title}</span>
                    <span className={styles.description}>
                        {x.description && `(${x.description})`}
                    </span>
                </div>
            ))}
        </div>
    );

    return (
        <div
            className={cx(
                styles.wrapper,
                isLight ? styles.light : null,
                fullWidth ? styles.fullwidth : null,
                className,
            )}
            ref={dropdownRef}
        >
            <div
                className={cx(
                    styles.content,
                    isOpened ? styles.content__open : null,
                    isLight ? styles.light__content : null,
                )}
                ref={containerRef}
            >
                <div
                    className={cx(
                        styles.header,
                        disabled ? styles.disabled : null,
                        isLight ? styles.header__light : null,
                        !selectedOption ? styles.header__placeholder : null,
                        userInteracted ? styles.header__userinteracted : null,
                    )}
                    ref={headerRef}
                    onClick={toggleDropdown}
                    role="presentation"
                >
                    {selectedOption && selectedOption.iconName && (
                        <Icon
                            name={selectedOption.iconName}
                            className={styles.dropdown__value__icon}
                        />
                    )}
                    {selectedOption && (
                        <section className={styles.header__selectedoption}>
                            {selectedOption
                                ? selectedOption.title
                                : !searchable && placeholderText}
                        </section>
                    )}
                    {searchable && !selectedOption && (
                        <input
                            type="text"
                            className={styles.searchfield}
                            placeholder={placeholderText}
                            value={searchQuery || ""}
                            onChange={(
                                e: React.ChangeEvent<HTMLInputElement>,
                            ) => setSearchQuery(e.target.value)}
                        />
                    )}
                    {searchable && (searchQuery || selectedOption) && (
                        <Button
                            type="function"
                            icon="close"
                            onClick={clearSearch}
                            size="small"
                        />
                    )}

                    {!disabled && pending && (
                        <Spinner size="small" className={styles.spinner} />
                    )}
                    {disabled || pending ? null : (
                        <Icon
                            name="dropdown-down"
                            className={cx(
                                styles.dropdown__icon,
                                isOpened ? styles.dropdown__icon__up : null,
                            )}
                        />
                    )}
                </div>
                {isOpened && ReactDOM.createPortal(listJSX, document.body)}
            </div>
        </div>
    );
};

export default React.memo(Dropdown);
